import { Country, State, City } from 'country-state-city';
import { IPermission } from 'src/config/types';
import { moduleName, permissions } from './permissions';
import { isBefore, isToday, startOfDay } from 'date-fns';
import colors from './colors';

const { getCitiesOfState } = City;

export const getCitiesOptions = (state: string) => {
  return getCitiesOfState('US', state).map((city) => ({
    label: city.name,
    value: city.name,
  }));
};

export const devEnvConfig: { [key: string]: any } = {
  dev: {
    baseUrl: 'https://wx-fe-dev.azurewebsites.net',
    apiBaseUrl: 'https://wx-be-dev.azurewebsites.net/api',
  },
  staging: {
    baseUrl: 'https://wx-fe.azurewebsites.net',
    apiBaseUrl: 'https://wx-app-backend-latest.azurewebsites.net/api',
  },
};

const currentEnv: { [key: string]: string } =
  devEnvConfig[process.env.env || 'dev'];

export const API_BASE_URI = currentEnv.apiBaseUrl;

export const MOCK_BASE_URL =
  'https://acc9e2c7-25cd-4540-9f5b-df67d1bf338f.mock.pstmn.io';

const isNotSafari = !navigator.vendor.toLowerCase().startsWith('apple');

export const days = [
  {
    value: 'monday',
    label: 'Monday',
  },
  {
    value: 'tuesday',
    label: 'Tuesday',
  },
  {
    value: 'wednesday',
    label: 'Wednesday',
  },
  {
    value: 'thursday',
    label: 'Thursday',
  },
  {
    value: 'friday',
    label: 'Friday',
  },
  {
    value: 'saturday',
    label: 'Saturday',
  },
  {
    value: 'sunday',
    label: 'Sunday',
  },
];

export const multiDays = [
  {
    value: 'monday',
    label: 'Monday',
    checked: false,
  },
  {
    value: 'tuesday',
    label: 'Tuesday',
    checked: false,
  },
  {
    value: 'wednesday',
    label: 'Wednesday',
    checked: false,
  },
  {
    value: 'thursday',
    label: 'Thursday',
    checked: false,
  },
  {
    value: 'friday',
    label: 'Friday',
    checked: false,
  },
  {
    value: 'saturday',
    label: 'Saturday',
    checked: false,
  },
  {
    value: 'sunday',
    label: 'Sunday',
    checked: false,
  },
];

export const getCountries = () => {
  return Country.getAllCountries().map((country) => {
    return {
      label: country.name,
      value: country.name,
      code: country.isoCode,
    };
  });
};
export const getCountriesByName = (name: string) => {
  return Country.getAllCountries()
    .filter((e) => e.name === name)
    .map((country) => {
      return {
        label: country.name,
        value: country.name,
        code: country.isoCode,
      };
    });
};

export const getStatesOfCountry = (countryCode: string) => {
  const tempStates = State.getStatesOfCountry(countryCode);
  const stats = tempStates.filter(
    (state) => getCitiesOfState('US', state.isoCode).length !== 0
  );

  return stats.map((state) => ({
    label: state.name,
    value: state.name,
  }));
};
export const getStatesOfCountryByStateName = (
  countryCode: string,
  state: string
) => {
  const stats = State.getStatesOfCountry(countryCode);
  return stats
    .map((state) => ({
      label: state.name,
      value: state.name,
      stateCode: state.isoCode,
    }))
    .filter((e) => e.label === state);
};

export const getAllStates = () => {
  const states = State.getAllStates();
  return states
    .map((state) => ({
      label: state.name,
      value: state.name,
    }))
    .sort((a: any, b: any) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
};

export const getAllStatesByCountryCode = (countryCode: string) => {
  const tempStates = State.getStatesOfCountry(countryCode);
  const states = tempStates.filter(
    (state) => getCitiesOfState('US', state.isoCode).length !== 0
  );

  return states
    .map((state) => ({
      label: state.name,
      value: state.name,
      stateCode: state.isoCode,
    }))
    .sort((a: any, b: any) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
};

const citiesByState = {
  Alabama: [
    { label: 'Birmingham', value: 'Birmingham' },
    { label: 'Montgomery', value: 'Montgomery' },
    // Add more cities for Alabama here if available
  ],
  Alaska: [
    { label: 'Anchorage', value: 'Anchorage' },
    { label: 'Fairbanks', value: 'Fairbanks' },
    // Add more cities for Alaska here if available
  ],
  'American Samoa': [
    { label: 'Pago Pago', value: 'Pago Pago' },
    // Add more cities for American Samoa here if available
  ],
  Arizona: [
    { label: 'Phoenix', value: 'Phoenix' },
    { label: 'Tucson', value: 'Tucson' },
    // Add more cities for Arizona here if available
  ],
  // ... (and so on for all the states)

  Wyoming: [
    { label: 'Cheyenne', value: 'Cheyenne' },
    { label: 'Casper', value: 'Casper' },
    // Add more cities for Wyoming here if available
  ],
};

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const usTimeZones = [
  {
    value: 'Eastern Daylight Time (GMT-4)',
    label: 'Eastern Daylight Time (GMT-4)',
  },
  {
    value: 'Central Daylight Time (GMT-5)',
    label: 'Central Daylight Time (GMT-5)',
  },
  {
    value: 'Mountain Daylight Time (GMT-6)',
    label: 'Mountain Daylight Time (GMT-6)',
  },
  {
    value: 'Mountain Standard Time (GMT-7)',
    label: 'Mountain Standard Time (GMT-7)',
  },
  {
    value: 'Alaska Daylight Time (GMT-8)',
    label: 'Alaska Daylight Time (GMT-8)',
  },
  {
    value: 'Hawaii-Aleutian Standard Time (GMT-10)',
    label: 'Hawaii-Aleutian Standard Time (GMT-10)',
  },
];

export const classData = {
  data: [
    {
      id: '940c9fbb-2c5d-4aaa-8429-556caa72955a',
      class_name: 'Legs exercises',
      total_slots: 100,
      filled_slots: 0,
      difficulty_level: 'First',
      tags: ['fitness', 'basic health'],
      class_time: '2022-11-07T09:34:28.925Z',
      class_start_time: '8:00AM',
      class_duration: 40,
      images_url: [
        'https://images.unsplash.com/photo-1598653222000-6b7b7a552625?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxzZWFyY2h8Mnx8bXVzaWMlMjBzdHVkaW98ZW58MHx8MHx8&w=1000&q=80',
        'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxzZWFyY2h8M3x8c3R1ZGlvfGVufDB8fDB8fA%3D%3D&w=1000&q=80',
      ],
      created_at: '2022-11-07T04:51:46.194Z',
      updated_at: null,
      deleted_at: null,
      trainers: [
        {
          trainer_name: 'Daniel James',
          trainer_class: 'Pilates Instructor',
          trainer_bio:
            'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes.',
          images_url: [
            'https://images.ctfassets.net/lh3zuq09vnm2/yBDals8aU8RWtb0xLnPkI/19b391bda8f43e16e64d40b55561e5cd/How_tracking_user_behavior_on_your_website_can_improve_customer_experience.png',
          ],
          id: '23445235',
        },
        {
          trainer_name: 'John Smith',
          trainer_class: 'Pilates Instructor',
          trainer_bio:
            'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes.',
          images_url: [
            'https://images.ctfassets.net/lh3zuq09vnm2/yBDals8aU8RWtb0xLnPkI/19b391bda8f43e16e64d40b55561e5cd/How_tracking_user_behavior_on_your_website_can_improve_customer_experience.png',
          ],
          id: '23445235',
        },
        {
          trainer_name: 'Jack James',
          trainer_class: 'Pilates Instructor',
          trainer_bio:
            'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes.',
          images_url: [
            'https://images.ctfassets.net/lh3zuq09vnm2/yBDals8aU8RWtb0xLnPkI/19b391bda8f43e16e64d40b55561e5cd/How_tracking_user_behavior_on_your_website_can_improve_customer_experience.png',
          ],
          id: '23445235',
        },
        {
          trainer_name: 'Brad Smith',
          trainer_class: 'Pilates Instructor',
          trainer_bio:
            'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes.',
          images_url: [
            'https://images.ctfassets.net/lh3zuq09vnm2/yBDals8aU8RWtb0xLnPkI/19b391bda8f43e16e64d40b55561e5cd/How_tracking_user_behavior_on_your_website_can_improve_customer_experience.png',
          ],
          id: '23445235',
        },
      ],
    },
    {
      id: 'c9f8d8e9-924c-4165-bedb-c9e2b81f3f11',
      class_name: 'Yoga',
      total_slots: 100,
      filled_slots: 0,
      difficulty_level: 'Moderate',
      tags: ['fitness', 'basic health'],
      class_time: '2022-11-03T06:59:15.976Z',
      class_start_time: '10:00AM',
      class_duration: 40,
      images_url: [
        'https://images.unsplash.com/photo-1598653222000-6b7b7a552625?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxzZWFyY2h8Mnx8bXVzaWMlMjBzdHVkaW98ZW58MHx8MHx8&w=1000&q=80',
        'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxzZWFyY2h8M3x8c3R1ZGlvfGVufDB8fDB8fA%3D%3D&w=1000&q=80',
      ],
      created_at: '2022-11-03T02:54:55.733Z',
      updated_at: null,
      trainers: [
        {
          trainer_name: 'Daniel James',
          trainer_class: 'Pilates Instructor',
          trainer_bio:
            'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes.',
          images_url: [
            'https://images.ctfassets.net/lh3zuq09vnm2/yBDals8aU8RWtb0xLnPkI/19b391bda8f43e16e64d40b55561e5cd/How_tracking_user_behavior_on_your_website_can_improve_customer_experience.png',
          ],
          id: '23445235',
        },
      ],
      deleted_at: null,
    },
    {
      id: '46a9c8c4-a855-4be5-85cf-9692fa28ec56',
      class_name: 'arms exercises',
      total_slots: 100,
      filled_slots: 0,
      difficulty_level: 'First',
      tags: ['fitness', 'basic health'],
      class_time: '2022-11-04T06:59:15.976Z',
      class_start_time: '12:00PM',
      class_duration: 40,
      images_url: [
        'https://images.unsplash.com/photo-1598653222000-6b7b7a552625?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxzZWFyY2h8Mnx8bXVzaWMlMjBzdHVkaW98ZW58MHx8MHx8&w=1000&q=80',
        'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxzZWFyY2h8M3x8c3R1ZGlvfGVufDB8fDB8fA%3D%3D&w=1000&q=80',
      ],
      trainers: [
        {
          trainer_name: 'Daniel James',
          trainer_class: 'Pilates Instructor',
          trainer_bio:
            'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes.',
          images_url: [
            'https://images.ctfassets.net/lh3zuq09vnm2/yBDals8aU8RWtb0xLnPkI/19b391bda8f43e16e64d40b55561e5cd/How_tracking_user_behavior_on_your_website_can_improve_customer_experience.png',
          ],
          id: '23445235',
        },
      ],
      created_at: '2022-11-03T04:29:37.432Z',
      updated_at: null,
      deleted_at: null,
    },
  ],
  statusCode: 200,
};

export const fileToBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    var fr = new FileReader();
    fr.onload = () => {
      resolve(fr.result);
    };
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
};

export const uniqueId = () => {
  return String(Date.now().toString(32) + Math.random().toString(16)).replace(
    /\./g,
    ''
  );
};

export const workingDays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const weekAndDay = [
  {
    value: 'Week',
    label: 'Week',
  },
  {
    value: 'Day',
    label: 'Day',
  },
];

export const calculateFileSize = (percentage: number, currentFile: File) => {
  if (!!percentage && currentFile) {
    return (
      ((percentage / 100) * (currentFile.size / 1000000)).toFixed(2) +
      'MB out of ' +
      (currentFile?.size / 1000000).toFixed(2) +
      'MB'
    );
  }
  if (currentFile) {
    return (currentFile.size / 1000000).toFixed(2) + 'MB';
  }
  return '0 KB';
};

export const calculateFileSizeForMember = (size: number) => {
  if (Number(size)) {
    return (size / 1000000).toFixed(2) + 'MB';
  }
  return '0 KB';
};

export const formatFileSize = (file: any) => {
  if (file) {
    const fileSizeInBytes = file.size;
    if (fileSizeInBytes >= 1024 * 1024 * 1024) {
      return (fileSizeInBytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    } else if (fileSizeInBytes >= 1024 * 1024) {
      return (fileSizeInBytes / (1024 * 1024)).toFixed(2) + ' MB';
    } else if (fileSizeInBytes >= 1024) {
      return (fileSizeInBytes / 1024).toFixed(2) + ' KB';
    } else {
      return fileSizeInBytes + ' bytes';
    }
  }
};

export const stringToCoordinatesObj = (coordinates: string) => {
  let splitCoordinates = coordinates.split(',');
  return {
    lat: splitCoordinates ? splitCoordinates[0] : '',
    lng: splitCoordinates ? splitCoordinates[1] : '',
  };
};

export const urlFilterGenerate = (filters: any) => {
  if (!filters) return '';
  let url = '';
  Object.keys(filters).forEach((filter: string) => {
    if (filters[filter] === '') return;
    if (url === '') {
      return (url = `?${filter}=${filters[filter]}`);
    }
    return (url = `${url}&${filter}=${filters[filter]}`);
  });
  return url;
};
export const settingsSidebarItems = [
  {
    to: '/settings/business-settings',
    title: 'Business Settings',
    key: 'BUSINESS_SETTINGS',
    nested: true,
    module: moduleName.BUSINESS_SETTINGS,
    action: permissions.VIEW_BUSINESS_BILLING_AND_PAYMENT_PAGE,
    items: [
      {
        to: 'business-profile',
        title: 'Business Profile',
        key: 'BUSINESS_PROFILE',
        module: moduleName.BUSINESS_SETTINGS,
        action: permissions.VIEW_BUSINESS_PROFILE_PAGE,
      },
      {
        to: 'business-subscription',
        title: 'Subscription',
        key: 'SUBSCRIPTION',
        module: moduleName.BUSINESS_SETTINGS,
        action: permissions.VIEW_BUSINESS_SUBSCRIPTION_PAGE,
      },
      {
        to: 'business-billings',
        title: 'Billing & Payment',
        key: 'BILLING_AND_PAYMENT',
        module: moduleName.BUSINESS_SETTINGS,
        action: permissions.VIEW_BUSINESS_BILLING_AND_PAYMENT_PAGE,
      },
      {
        to: 'business-revenue',
        title: 'Revenue Modal',
        key: 'REVENUE_MODEL',
        module: moduleName.BUSINESS_SETTINGS,
        action: permissions.VIEW_BUSINESS_REVENUE_MODEL_PAGE,
      },
    ],
  },
  {
    nested: true,
    to: '/settings/builder',
    key: 'BUILDER',
    title: 'Plan & Packages',
    module: moduleName.PLAN_AND_PACKAGE,
    action: permissions.CREATE,
    items: [
      {
        to: 'plan',
        title: 'Monthly Subscription',
        key: 'MONTHLY_SUBSCRIPTION',
        module: moduleName.PLAN_AND_PACKAGE,
        action: permissions.CREATE,
      },
      {
        to: 'package',
        title: 'Day Pass Pricing',
        key: 'PASS_SUBSCRIPTION',
        module: moduleName.PLAN_AND_PACKAGE,
        action: permissions.CREATE,
      },
    ],
  },
  {
    to: '/settings/studio-staff-management',
    title: 'Studio Staff Management',
    key: 'STUDIO_STAFF_MANAGEMENT',
    nested: false,
    module: moduleName.STUDIO_STAFF,
    action: permissions.VIEW_STAFF_MANAGEMENT_PAGE,
  },
];
export const classDifficultyOptions = [
  { value: 'very light', label: 'Very Light' },
  { value: 'light', label: 'Light' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'hard', label: 'Hard' },
  { value: 'very hard', label: 'Very Hard' },
];

export const classFrequency = [
  { label: 'One Time', value: 'one-time' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Bi-Weekly', value: 'biWeekly' },
];

export const dateFormate = (date: Date) => {
  let res = new Intl.DateTimeFormat('fr-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
  return res;
};

export const dateFormateForOnlyMonthAndYear = (date: Date | string) => {
  let newDate = new Date(date);
  let year = newDate.toLocaleString('default', { year: 'numeric' });
  let month = newDate.toLocaleString('default', { month: 'short' });
  return `${month}, ${year}`;
};

export const calculateClassZIndex = (currentIndex: number) => {
  switch (currentIndex) {
    case 0:
      return 'z-40';
    case 1:
      return 'z-30';
    case 2:
      return 'z-20';
    default:
      return 'z-10';
  }
};

export const calculatePercentage = (totalValue: any, discount: number) => {
  if (isNaN(totalValue)) return 0;
  let res = (totalValue * discount) / 100;
  return res;
};

export const dateFormatForMemberCheckoutPage = (
  date: any,
  divider: string = '/'
) => {
  let year = date.toLocaleString('default', { year: '2-digit' });
  let month = date.toLocaleString('default', { month: '2-digit' });
  let day = date.toLocaleString('default', { day: '2-digit' });

  let formattedDate = day + divider + month + divider + year;
  return formattedDate;
};

export const enrollmentClasses: any[] = [
  {
    name: 'Water Aerobics',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    isOneTime: false,
    oneTime: '',
    sessionList: [
      { day: 'monday', start_time: '09:00AM', end_time: '10:00AM' },
      { day: 'Wednesday', start_time: '12:30PM', end_time: '01:30PM' },
      { day: 'Friday', start_time: '09:00AM', end_time: '10:00AM' },
      { day: 'Thursday', start_time: '12:30PM', end_time: '01:30PM' },
    ],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 1',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'Stretches',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    isOneTime: true,
    oneTime: '12:30PM - 01:30PM',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 3',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'High-intensity interval',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    isOneTime: true,
    oneTime: '12:30PM - 01:30PM',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 3',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'Water Aerobics',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    isOneTime: true,
    oneTime: '12:30PM - 01:30PM',
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 1',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'Stretches',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    isOneTime: false,
    oneTime: '',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 3',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'High-intensity interval',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 3',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'Water Aerobics',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 1',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'Stretches',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 3',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'High-intensity interval',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 3',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'Water Aerobics',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 1',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'Stretches',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 3',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
  {
    name: 'High-intensity interval',
    trainers: [
      {
        name: 'Simon',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'Simth',
        url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
      },
      {
        name: 'John',
        url: '',
      },
    ],
    sessionType: 'Weekly, Mon-Wed-Thu-Fri',
    sessionList: [],
    description:
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit. Exercitation veniam consequat sunt nostrud amet.',
    location: 'Location 3',
    time: '60 Mins',
    picture:
      'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    classDifficulty: 'Very Light',
  },
];

export const multiDaysForClassEnrollment = [
  {
    value: 'all-session',
    label: 'All Sessions',
  },
  {
    value: 'monday',
    label: 'Monday',
  },
  {
    value: 'tuesday',
    label: 'Tuesday',
  },
  {
    value: 'wednesday',
    label: 'Wednesday',
  },
  {
    value: 'thursday',
    label: 'Thursday',
  },
  {
    value: 'friday',
    label: 'Friday',
  },
];

export const classEnrollmentSessions = [
  {
    day: 'Monday',
    slots_total: 30,
    slots_booked: 18,
    date: '8/3/22',
    space: 'Space 3',
    start_time: '09:00AM',
    end_time: '10:00AM',
    isActive: true,
  },
  {
    day: 'Wednesday',
    slots_total: 30,
    slots_booked: 30,
    date: '18/3/22',
    space: 'Space 1',
    start_time: '09:00AM',
    end_time: '10:00AM',
    isActive: false,
  },
  {
    day: 'Thursday',
    slots_total: 30,
    slots_booked: 12,
    date: '18/3/22',
    space: 'Space 3',
    start_time: '09:00AM',
    end_time: '10:00AM',
    isActive: false,
  },
  {
    day: 'Friday',
    slots_total: 30,
    slots_booked: 30,
    date: '18/3/22',
    space: 'Space 5',
    start_time: '09:00AM',
    end_time: '10:00AM',
    isActive: false,
  },
];

export const classEnrollmentTableData = [
  {
    id: '1',
    first_name: 'Marvin',
    last_name: 'McKinney',
    profile_pic: '',
    membership_id: 'AM-00223',
    enrollment_status: 'enrolled',
    check_in_status: 'not_checked_in',
  },
  {
    id: '2',
    first_name: 'Devon',
    last_name: 'Lane',
    profile_pic:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
    membership_id: 'OL-09182',
    enrollment_status: 'unenrolled',
    check_in_status: '',
  },
  {
    id: '3',
    first_name: 'Kristin',
    last_name: 'Watson',
    profile_pic: '',
    membership_id: 'AM-09182',
    enrollment_status: 'waitlisted',
    check_in_status: '',
  },
  {
    id: '4',
    first_name: 'Marvin',
    last_name: 'McKinney',
    profile_pic:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
    membership_id: 'AM-00223',
    enrollment_status: 'enrolled',
    check_in_status: 'checked_in',
  },
  {
    id: '5',
    first_name: 'Devon',
    last_name: 'Lane',
    profile_pic: '',
    membership_id: 'OL-09182',
    enrollment_status: 'unenrolled',
    check_in_status: 'checked_in',
  },
  {
    id: '6',
    first_name: 'Kristin',
    last_name: 'Watson',
    profile_pic:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
    membership_id: 'AM-09182',
    enrollment_status: 'waitlisted',
    check_in_status: '',
  },
];

export const calculateDateForPayAccordingToPackageDuration = (
  months: number
) => {
  let todayDate = new Date();
  try {
    let newDate = new Date(todayDate.setMonth(todayDate.getMonth() + months));
    return newDate;
  } catch (error) {
    return new Date();
  }
};
export const membersList = [
  {
    url: '',
    name: 'Daniel James',
    status: 'active',
    email: 'daniel@gmail.com',
    memberId: 'AM-00342',
    id: '1',
  },
  {
    url: 'https://images.unsplash.com/photo-1491528323818-fdd1faba62cc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    name: 'Daniel K.',
    status: 'active',
    email: 'danielk@gmail.com',
    memberId: 'AM-00344',
    id: '2',
  },
  {
    url: '',
    name: 'Daniel K.',
    status: 'active',
    email: 'danielk@gmail.com',
    memberId: 'AM-00345',
    id: '3',
  },
  {
    url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2.25&w=256&h=256&q=80',
    name: 'John Doe',
    status: 'active',
    email: 'danielk@gmail.com',
    memberId: 'AM-00346',
    id: '4',
  },
  {
    url: 'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    name: 'Sandy.',
    status: 'active',
    email: 'danielk@gmail.com',
    memberId: 'AM-00347',
    id: '5',
  },
  {
    url: 'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    name: 'James',
    status: 'active',
    email: 'danielk@gmail.com',
    memberId: 'AM-00348',
    id: '6',
  },
  {
    url: 'https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    name: 'Charles',
    status: 'active',
    email: 'danielk@gmail.com',
    memberId: 'AM-00349',
    id: '7',
  },
];

export const classesList = [
  {
    name: 'Water Aerobics',
    classDifficulty: 'Very Hard',
    from: 'From: 12/4/17',
    to: '30/5/17',
  },
  {
    name: 'Water Aerobics',
    classDifficulty: 'Very Hard',
    from: 'From: 12/4/17',
    to: '30/5/17',
  },
  {
    name: 'Water Aerobics',
    classDifficulty: 'Very Hard',
    from: 'From: 12/4/17',
    to: '30/5/17',
  },
  {
    name: 'Water Aerobics',
    classDifficulty: 'Very Hard',
    from: 'From: 12/4/17',
    to: '30/5/17',
  },
  {
    name: 'Water Aerobics',
    classDifficulty: 'Very Hard',
    from: 'From: 12/4/17',
    to: '30/5/17',
  },
  {
    name: 'Water Aerobics',
    classDifficulty: 'Very Hard',
    from: 'From: 12/4/17',
    to: '30/5/17',
  },
];

export const SessionsByDay = [
  {
    day: 'monday',
    start_time: '09:00',
    end_time: '10:00',
    sessionList: [
      { date: '12/12/20', id: '1', onRemove: () => {} },
      { date: '12/21/20', id: '2', onRemove: () => {} },
    ],
  },
  { day: 'tuesday', start_time: '', end_time: '', sessionList: [] },
  { day: 'wednesday', start_time: '', end_time: '', sessionList: [] },
  { day: 'thursday', start_time: '', end_time: '', sessionList: [] },
  { day: 'friday', start_time: '', end_time: '', sessionList: [] },
];

export const dateFormat = (date: Date) => {
  let year = date.toLocaleString('default', { year: 'numeric' });
  let month = date.toLocaleString('default', { month: '2-digit' });
  let day = date.toLocaleString('default', { day: '2-digit' });

  let formattedDate = day + '/' + month + '/' + year;
  return formattedDate;
};

export const dateFormatForMember = (date: any, divider: string = '/') => {
  let year = date.toLocaleString('default', { year: 'numeric' });
  let month = date.toLocaleString('default', { month: '2-digit' });
  let day = date.toLocaleString('default', { day: '2-digit' });

  let formattedDate = year + divider + month + divider + day;
  return formattedDate;
};

export const allowedExtensionsForProfile = /(\.jpg|\.jpeg|\.png)$/i;
export const allowedExtensionsForContractFile = /(\.pdf|\.doc|\.docx)$/i;
export const allowedExtensionsForMemberCSVFile = /(\.csv)$/i;

export const checkIfNumberOrFloat = (value: string) => {
  let validNumber = new RegExp(/^\d+(\.\d+)?$/);
  return validNumber.test(value);
};

export const ErrorMessageFromApi = 'Something went wrong, Try again later';

export const checkIfValueIsNaN = (value: any) => {
  if (isNaN(value)) {
    return 0;
  }
  return value;
};

export const memberDetailsAddressParser = (value: any) => {
  let defaultValues = {
    city: '',
    state: '',
    zip_code: '',
    address: '',
  };

  if (!value) return defaultValues;
  try {
    return JSON.parse(value.replace(/'/g, '"'));
  } catch (err) {
    return defaultValues;
  }
};

export const contractFileNameMaker = (id: string, fileType: string) => {
  if (!id || !fileType) return '';
  return `${id}.${fileType.split('/')[1]}`;
};
export const packageExpiryDateGetter = (date: string, duration: number) => {
  if (!date || !duration) return '';
  try {
    let jsDate = new Date(date);
    var newDate = new Date(jsDate.setMonth(jsDate.getMonth() + duration));
    return standardDateFormat(newDate);
  } catch (error) {
    return '';
  }
};

export const hasPermission = (
  permissions: IPermission[] | undefined,
  action: string,
  sectionName: string
) => {
  if (!permissions) return;
  return permissions.some((permission) => {
    return (
      permission.action.trim() === action.trim() &&
      permission.resource.trim() === sectionName.trim()
    );
  });
};

export const standardDateFormat = (date: Date | string): string => {
  if (!date) return '';
  try {
    let newDate = new Date(date);
    let year = newDate.toLocaleString('default', { year: '2-digit' });
    let month = newDate.toLocaleString('default', { month: '2-digit' });
    let day = newDate.toLocaleString('default', { day: '2-digit' });

    return month + '/' + day + '/' + year;
  } catch (error) {
    return date.toLocaleString();
  }
};

export const dateFormatForMembership = (date: Date | string): string => {
  if (!date) return '';
  try {
    let newDate = new Date(date);
    let year = newDate.toLocaleString('default', { year: 'numeric' });
    let month = newDate.toLocaleString('default', { month: '2-digit' });
    let day = newDate.toLocaleString('default', { day: '2-digit' });

    return year + '-' + month + '-' + day;
  } catch (error) {
    return date.toLocaleString();
  }
};

export const valueToDecimal = (num: string | number | null) => {
  try {
    if (!Number(num)) return num;
    return (Math.round(Number(num) * 100) / 100).toFixed(2);
  } catch (error) {
    return num;
  }
};

export const standardPhoneNumberFormat = (value: string) => {
  if (!value) return '';
  try {
    return `+${value.slice(0, 1)} (${value.slice(1, 4)}) ${value.slice(
      4,
      7
    )}-${value.slice(7)}`;
  } catch (err) {
    return value;
  }
};

export const toSentenceCase = (value: string | number) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  try {
    if (value.length > 0) {
      let res = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      return res;
    }
  } catch (err) {
    return value;
  }
};

export const dataJsonParser = (data: any) => {
  try {
    const parsedData = JSON.parse(data);
    return parsedData;
  } catch (error: any) {
    console.error('Error parsing JSON:', error.message);
    return null;
  }
};

export const checkIfIsOneDayLeftFromToday = (value: string) => {
  const targetDate = new Date(value);
  const today = startOfDay(new Date());
  const oneDayBeforeToday = new Date(today);
  oneDayBeforeToday.setDate(today.getDate() - 1);

  let checkIfIsOneDayLeft =
    isBefore(targetDate, today) && isBefore(oneDayBeforeToday, targetDate);
  let checkIfTodayIsDueDate = isToday(targetDate);
  let checkIfDueDateHasPassed = isBefore(targetDate, startOfDay(today));

  if (checkIfIsOneDayLeft || checkIfTodayIsDueDate || checkIfDueDateHasPassed) {
    return ' text-red-600';
  }
  return ' text-black';
};

export const getCurrentRole = (obj: { [x: string]: any }) => {
  for (const key in obj) {
    if (obj[key]) return key;
  }
  return undefined;
};

export const getAllRoles = (values: any) => {
  const { is_client_admin, is_lead, is_resource, is_staffer, is_super_admin } =
    values || {};
  return {
    is_client_admin,
    is_lead,
    is_resource,
    is_staffer,
    is_super_admin,
  };
};

const {
  tooLight,
  veryLight,
  perfect,
  tooBusy,
  needHelp,
  staffingStatus_01,
  staffingStatus_02,
  staffingStatus_03,
  staffingStatus_04,
  staffingStatus_05,
  noLoad,
} = colors || {};
export const noImageUrl =
  'https://www.aalforum.eu/wp-content/uploads/2016/04/profile-placeholder.png';

export const staffingStatusBgColors = (status: string) => {
  switch (status) {
    case 'To_Light' || 'to_Light':
      return staffingStatus_01;
    case 'Light' || 'light':
      return staffingStatus_02;
    case 'Need help' || 'need_help':
      return staffingStatus_05;
    case 'Busy' || 'busy':
      return staffingStatus_04;
    case 'Perfect' || 'perfect' || 'bench' || 'Bench':
      return staffingStatus_03;

    default:
      return 'text-blue-950';
  }
};
export const staffingStatusBgColors_lower = (status: string) => {
  switch (status) {
    case 'to_Light':
      return staffingStatus_01;
    case 'light':
      return staffingStatus_02;
    case 'need_help':
      return staffingStatus_05;
    case 'busy':
      return staffingStatus_04;
    case 'perfect':
      return staffingStatus_03;
    case 'bench':
      return staffingStatus_03;

    default:
      return colors.staffingStatus_01;
  }
};
export const staffingStatusTextColors = (status: string) => {
  switch (status) {
    case 'To_Light':
      return tooLight;
    case 'Light':
      return veryLight;
    case 'Need Help!':
      return needHelp;
    case 'Busy':
      return tooBusy;
    case 'Perfect':
      return perfect;

    default:
      return colors.white;
  }
};
export const staffingStatusTextColors_lower = (status: string) => {
  switch (status) {
    case 'to_Light':
      return tooLight;
    case 'to_light':
      return tooLight;
    case 'light':
      return veryLight;
    case 'need_help':
      return needHelp;
    case 'busy':
      return tooBusy;
    case 'perfect':
      return perfect;
    case 'bench':
      return perfect;

    default:
      return colors.white;
  }
};
export const projectRanks = Object.freeze({
  Active: 'active',
  Inactive: 'inactive',
  OnHold: 'on hold',
  Completed: 'completed',
  Archive: 'archieved',
  Draft: 'draft',
  Behind: 'behind',
});
export const staffingRanks = Object.freeze({
  tooLight: 'to_light',
  light: 'light',
  needHelp: 'need_help',
  busy: 'busy',
  perfect: 'perfect',
});

export const getColorForActivityLevel = (value: number) => {
  if (value === 0) return noLoad;
  if (value < 20 && value > 0) return tooLight;

  if (value >= 20 && value < 40) return tooLight;
  if (value >= 40 && value < 60) return perfect;
  if (value >= 60 && value < 80) return tooBusy;
  if (value >= 80) return needHelp;

  return noLoad;
};

export const projectStatusColors: any = {
  active: {
    textColor: 'rgba(222, 74, 91, 1)',
    backgroundColor: 'rgba(222,74,91,0.15)',
  },
  'on hold': {
    textColor: 'rgba(241, 165, 108, 1)',
    backgroundColor: 'rgba(241, 157, 93,0.15)',
  },
  behind: {
    textColor: 'rgba(78, 94, 145, 1)',
    backgroundColor: 'rgb(231, 231, 248)',
  },
  completed: {
    textColor: 'rgba(50, 164, 141, 1)',
    backgroundColor: 'rgba(50, 164, 141,0.15)',
  },
  // Archive, inactive, and draft same as behind.
  inactive: {
    textColor: 'rgba(78, 94, 145, 1)',
    backgroundColor: 'rgb(231, 231, 248)',
  },
  draft: {
    textColor: 'rgba(78, 94, 145, 1)',
    backgroundColor: 'rgb(231, 231, 248)',
  },
  archived: {
    textColor: 'rgba(78, 94, 145, 1)',
    backgroundColor: 'rgb(231, 231, 248)',
  },
};

export const StaffingStatusColors: any = {
  to_light: {
    textColor: 'rgba(149, 150, 241, 1)',
    backgroundColor: 'rgba(149, 150, 241, 0.2)',
  },
  light: {
    textColor: 'rgba(175, 68, 206, 1)',
    backgroundColor: 'rgba(175, 68, 206, 0.2)',
  },
  bench: {
    textColor: 'rgba(50, 164, 141, 1)',
    backgroundColor: 'rgba(50, 164, 141, 0.2)',
  },
  need_help: {
    textColor: 'rgba(222, 74, 91, 1)',
    backgroundColor: 'rgba(222, 74, 91, 0.2)',
  },
  busy: {
    textColor: 'rgba(241, 165, 108, 1)',
    backgroundColor: 'rgba(241, 165, 108, 0.2)',
  },
  perfect: {
    textColor: 'rgba(85, 87, 201, 1)',
    backgroundColor: 'rgba(85, 87, 201, 0.2)',
  },
};
